import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiEye, FiEyeOff, FiMail, FiLock, FiArrowRight } from 'react-icons/fi';
import apiUtils from 'utils/apiutils';
import { validateInput } from 'helpers/inputValidator';
import Logo from 'assets/Logo.webp'
import { toast } from 'sonner';
import SmallLogo from 'assets/Logo.webp'
import { isValidEmail } from 'helpers/emailValidator';
import { useCooldownAction } from 'hooks/useCooldownAction';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    totp: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [googlePendingData, setGooglePendingData] = useState(null);
  const [openmodal, setopenmodal] = useState(false)
  const googleButtonRef = useRef(null);
  const runWithCooldown = useCooldownAction();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [totprequired, settotprequired] = useState(false)
  const [debounceTimer, setDebounceTimer] = useState(null);
  const google_id = `${import.meta.env.VITE_GOOGLE_CLIENT_ID}`

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    await runWithCooldown(async () => {
      setLoading(true);
      try {
        const payload = {
          email: formData.email?.toLowerCase(),
          password: formData.password,
          login_type: "manual",
          totp: formData.totp || '',
          project_id: import.meta.env.VITE_PROJECT_ID
        };
        const response = await apiUtils.post('/user/login', payload, { showToast: true });

        if (response && response.JWT) {
          localStorage.setItem('jwt', response.JWT);
          navigate('/dashboard');
        } else {
          setLoading(false);
        }
      } catch (err) {
        setLoading(false)
      } finally {
        setLoading(false)
      }
    })
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Allow empty input to enable deletion/backspace
    if (value === "" || validateInput(name, value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const checkTotpStatus = async () => {
    try {
      const payload = {
        email: formData.email.toLowerCase(),
      };

      const response = await apiUtils.post('/user/check_totp_status', payload, {
        showToast: false,
      });

      if (response) {

        settotprequired(response.Success)
        toast.success("TOTP required");
      }
    } catch (err) {
      // toast.error(err.response?.data?.Error);
    }
  };


  const handleGoogleCallback = async (response) => {
    const idToken = response.credential;

    // Decode token to get email
    const base64Url = idToken.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decodedPayload = JSON.parse(window.atob(base64));
    const email = decodedPayload.email;
    const email1 = email?.toLowerCase();

    try {
      const payload = { email: email1?.toLowerCase() };

      const totpResponse = await apiUtils.post('/user/check_totp_status', payload, {
        showToast: false,
      });

      if (totpResponse?.Success) {
        setopenmodal(true);
        setGooglePendingData({ response, email1 }); // store until user enters TOTP
        toast.success("TOTP required. Please enter it.");
        return; // wait for user to enter TOTP
      }

      // If no TOTP required, proceed with login
      await proceedWithGoogleLogin(response, email1);
    } catch (err) {
      // toast.error(err.response?.data?.Error || "Failed to verify TOTP status");
      await proceedWithGoogleLogin(response, email1);
    }
  };


  useEffect(() => {
    let retries = 0;
    const maxRetries = 20;
    const retryDelay = 100;

    const tryRenderGoogleSignin = () => {
      if (window.google?.accounts?.id && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: google_id,
          callback: handleGoogleCallback,
          auto_select: false,
          context: 'signin',
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'filled_black',
          size: 'large',
          text: 'signin_with',
          shape: 'pill',
          logo_alignment: 'left',
        });

        return true;
      }
      return false;
    };

    if (!tryRenderGoogleSignin()) {
      const interval = setInterval(() => {
        retries++;
        if (tryRenderGoogleSignin() || retries >= maxRetries) {
          clearInterval(interval);
        }
      }, retryDelay);
      return () => clearInterval(interval);
    }
  }, []); // Empty dependency array ensures this runs once on mount

  const proceedWithGoogleLogin = async (response, username) => {
    try {
      setLoading(true);

      const payload = {
        email: username?.toLowerCase(),
        password: "",
        login_type: "google",
        google_id_token: response?.credential,
        totp: formData.totp || '',
        project_id: import.meta.env.VITE_PROJECT_ID
      };

      const res = await apiUtils.post('/user/login', payload, { showToast: true });
      if (res?.JWT) {
        localStorage.setItem("jwt", res.JWT);
        navigate('/dashboard');
      } else {
        setLoading(false);
      }
    } catch (err) {

      setLoading(false);
    }
  };

  //

  useEffect(() => {
    if (!formData.email) return;

    if (debounceTimer) clearTimeout(debounceTimer);

    const timer = setTimeout(() => {
      checkTotpStatus();
    }, 500);

    setDebounceTimer(timer);
    return () => clearTimeout(timer);
  }, [formData.email]);


  return (
    <>
      <div className="min-h-screen flex items-center justify-center p-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md z-10"
        >
          <div className="card">
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}

                className="bg-gradient-primary  rounded-full mx-auto mb-4 flex items-center justify-center"
              >
                <img src={Logo} alt="" className='w-[250px]' />
              </motion.div>
              {/* <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1> */}
              <p className="text-white text-lg">Sign in to your account</p>
              <div className="mt-6 text-center">
                <div ref={googleButtonRef} className="flex justify-center" />
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field pl-10"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field pl-10 pr-10"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </motion.div>
              {totprequired ? <><motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  TOTP
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    name="totp"
                    value={formData.totp}
                    onChange={handleChange}
                    className="input-field pl-10"
                    placeholder="Enter TOTP"
                  // required
                  />
                </div>
              </motion.div></> : <></>}

              <div className="flex items-center justify-end">
                {/* <Link
                  to="/forgot-username"
                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  Forgot username?
                </Link> */}
                {/* <Link
                  to="/forgot-password"
                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  Forgot password?
                </Link> */}
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-secondary p-3 rounded-lg flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <h1>Loading...</h1>
                ) : (
                  <>
                    Sign In
                    <FiArrowRight />
                  </>
                )}
              </motion.button>
            </form>
            <div className="mt-8 text-center">
              <p className="text-slate-400">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-logiculustheme-colorPrimary  font-semibold transition-colors"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      {/* TOTP Modal */}
      {openmodal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-black rounded-lg p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-semibold text-logiculustheme-colorPrimary">TOTP Verification</h2>
            <input
              type="text"
              value={formData?.totp}
              name="totp"
              onChange={handleChange}
              className="input-field w-full"
              placeholder="Enter TOTP"
            />
            <button
              onClick={() => {
                // manually trigger Google login after TOTP input
                if (googlePendingData && formData?.totp.length > 0) {
                  setopenmodal(false)
                  proceedWithGoogleLogin(googlePendingData.response, googlePendingData.username);
                  setGooglePendingData(null); // reset
                }
              }}
              className="w-full bg-logiculustheme-neonBlue text-white py-2 rounded  transition"
            >
              Continue
            </button>
          </div>
        </div>
      )}

    </>
  );
};

export default Login;